'use client';

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '@shared/ui';
import type { BaseModalProps } from '@shared/types/modal.types';
import {
  getNotificationPreferences,
  updateNotificationPreferences,
} from '@/src/widgets/notifications/lib/notifications.api';
import { useMyClientProfile, useUpdateClientProfile } from '@/src/entities/user';

interface SettingsModalProps extends BaseModalProps {
  initialSection?: 'profile' | 'notifications' | 'privacy' | 'security';
}

/**
 * Settings Modal - user settings management
 * Usage: const { open } = useModal('settings'); open({ initialSection: 'privacy' });
 */
export function SettingsModal({
  isOpen,
  onClose,
  initialSection = 'profile',
}: SettingsModalProps) {
  const [activeSection, setActiveSection] = useState(initialSection);
  const [settings, setSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    marketingEmails: false,
    profileVisibility: 'public' as 'public' | 'private',
    showEmail: false,
    showPhone: false,
    twoFactorAuth: false,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load current notification preferences and client privacy settings.
  const { data: notificationPrefs } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: getNotificationPreferences,
    enabled: isOpen,
  });
  const { data: clientProfile } = useMyClientProfile(isOpen);
  const updateClientProfile = useUpdateClientProfile();

  useEffect(() => {
    setSettings((prev) => ({
      ...prev,
      ...(notificationPrefs
        ? {
            emailNotifications: notificationPrefs.emailEnabled,
            pushNotifications: notificationPrefs.pushEnabled,
            marketingEmails: notificationPrefs.emailMarketing,
          }
        : {}),
      ...(clientProfile
        ? {
            showEmail: clientProfile.showEmail,
            showPhone: clientProfile.showPhone,
          }
        : {}),
    }));
  }, [notificationPrefs, clientProfile]);

  const sections = [
    { id: 'profile' as const, label: 'Profile', icon: '👤' },
    { id: 'notifications' as const, label: 'Notifications', icon: '🔔' },
    { id: 'privacy' as const, label: 'Privacy', icon: '🔒' },
    { id: 'security' as const, label: 'Security', icon: '🛡️' },
  ];

  const handleToggle = (key: keyof typeof settings) => {
    setSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await Promise.all([
        updateNotificationPreferences({
          emailEnabled: settings.emailNotifications,
          pushEnabled: settings.pushNotifications,
          emailMarketing: settings.marketingEmails,
        }),
        updateClientProfile.mutateAsync({
          showEmail: settings.showEmail,
          showPhone: settings.showPhone,
        }),
      ]);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      animation="slide-up"
      title="Settings"
      description="Manage your account settings and preferences"
    >
      <div className="flex gap-6">
        {/* Sidebar */}
        <div className="w-48 flex-shrink-0">
          <nav className="space-y-1">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg text-left transition-colors ${
                  activeSection === section.id
                    ? 'bg-blue-50 text-blue-700 font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="text-xl">{section.icon}</span>
                <span>{section.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-[400px]">
          {/* Profile Section */}
          {activeSection === 'profile' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Profile Settings
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Profile Visibility{' '}
                  <span className="text-xs font-normal text-gray-400">(coming soon)</span>
                </label>
                <select
                  value={settings.profileVisibility}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      profileVisibility: e.target.value as 'public' | 'private',
                    }))
                  }
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed focus:outline-none"
                >
                  <option value="public">Public</option>
                  <option value="private">Private</option>
                </select>
              </div>
              <div className="space-y-3">
                <label className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Show email on profile</span>
                  <button
                    type="button"
                    onClick={() => handleToggle('showEmail')}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.showEmail ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.showEmail ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </label>
                <label className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">Show phone on profile</span>
                  <button
                    type="button"
                    onClick={() => handleToggle('showPhone')}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.showPhone ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.showPhone ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </label>
              </div>
            </div>
          )}

          {/* Notifications Section */}
          {activeSection === 'notifications' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Notification Preferences
              </h3>
              <div className="space-y-4">
                <label className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-gray-700">
                      Email Notifications
                    </div>
                    <div className="text-xs text-gray-500">
                      Receive updates via email
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleToggle('emailNotifications')}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.emailNotifications ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.emailNotifications ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </label>
                <label className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-gray-700">
                      Push Notifications
                    </div>
                    <div className="text-xs text-gray-500">
                      Receive push notifications
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleToggle('pushNotifications')}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.pushNotifications ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.pushNotifications ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </label>
                <label className="flex items-center justify-between opacity-60">
                  <div>
                    <div className="text-sm font-medium text-gray-700">
                      SMS Notifications{' '}
                      <span className="text-xs font-normal text-gray-400">(coming soon)</span>
                    </div>
                    <div className="text-xs text-gray-500">Receive text messages</div>
                  </div>
                  <button
                    type="button"
                    disabled
                    className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-300 cursor-not-allowed"
                  >
                    <span className="inline-block h-4 w-4 transform rounded-full bg-white translate-x-1" />
                  </button>
                </label>
                <label className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-gray-700">
                      Marketing Emails
                    </div>
                    <div className="text-xs text-gray-500">
                      Receive promotional content
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleToggle('marketingEmails')}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.marketingEmails ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.marketingEmails ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </label>
              </div>
            </div>
          )}

          {/* Privacy Section */}
          {activeSection === 'privacy' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Privacy Settings
              </h3>
              <p className="text-sm text-gray-600">
                Control who can see your information and how your data is used.
              </p>
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  Your privacy is important to us. We comply with PIPEDA regulations
                  to protect your personal information.
                </p>
              </div>
            </div>
          )}

          {/* Security Section */}
          {activeSection === 'security' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Security Settings
              </h3>
              <label className="flex items-center justify-between opacity-60">
                <div>
                  <div className="text-sm font-medium text-gray-700">
                    Two-Factor Authentication{' '}
                    <span className="text-xs font-normal text-gray-400">(coming soon)</span>
                  </div>
                  <div className="text-xs text-gray-500">
                    Add an extra layer of security
                  </div>
                </div>
                <button
                  type="button"
                  disabled
                  className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-300 cursor-not-allowed"
                >
                  <span className="inline-block h-4 w-4 transform rounded-full bg-white translate-x-1" />
                </button>
              </label>
              <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                Change Password
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Footer Actions */}
      <div className="pt-6 border-t border-gray-200 mt-6">
        {error && (
          <p className="mb-3 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
